import { useZonesForStructureQuery } from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Snackbar } from '@/components/shared/Snackbar';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import * as state from '@/state';
import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { BasicTabs } from '../shared/BasicTabs';
import { PageLoader } from '../shared/PageLoader';
import { StructureCheck } from '../shared/StructureCheck';
import { Header } from './Header';
import { containerStyles, FloatingLine, taskTabStyles, zonesManagementStyles } from './styles';
import { ZonesByStatus } from './ZonesByStatus';
import { ZonesComplexityViewer } from './ZonesTable';

const TabIcon = ({ iconName, isSelected }: { iconName: string; isSelected: boolean }) => (
  <AbyssIcon
    iconName={`${iconName}${!isSelected ? '-secondary' : ''}`}
    style={{ width: '1.6rem', height: '1.6rem' }}
  />
);


export const ZonesManagement = () => {
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const setSelectedZone = useSetAtom(state.selectedZone);
  const setZones = useSetAtom(state.zones);
  const structureId = useGetStructureId();
  const navbarHeight = useNavbarHeight();
  const { shouldShow, content } = snackbarMessage;

  useEffect(() => {
    setSelectedZone(null);
  }, [setSelectedZone]);

  const { data, loading, error } = useZonesForStructureQuery({
    variables: { structureId },
    skip: !structureId,
    onCompleted: (data) => {
      setZones(data.zonesForStructure);
    },
  });

  const zones = useMemo(() => data?.zonesForStructure ?? [], [data])

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const changeTab = useCallback((tabIndex: number) => {
    setSelectedTabIndex(tabIndex);
  }, []);

  const tabs = useMemo(() => {
    const TAB_CONFIG = [
      {
        iconName: 'status',
        label: 'By Status',
        component: <ZonesByStatus />,
      },
      {
        iconName: 'task',
        label: 'All Tasks',
        component: <ZonesComplexityViewer zones={zones} />
      },
    ];

    return TAB_CONFIG.map((tab, index) => ({
      label: (
        <Stack direction="row" alignItems="center" gap="0.8rem">
          <TabIcon iconName={tab.iconName} isSelected={selectedTabIndex === index} />
          <span>{tab.label}</span>
        </Stack>
      ),
      component: tab.component,
      index,
    }));
  }, [selectedTabIndex, zones]);

  if (loading) {
    return (
      <Box sx={{ position: 'relative', top: '25rem' }}>
        <PageLoader />
      </Box>
    );
  }

  if (error && !data) {
    return <>{`Error: ${error.message}`}</>;
  }

  if (!zones) {
    return <>No zones found</>;
  }

  return (
    <StructureCheck structureId={structureId}>
      <Stack
        direction="row"
        justifyContent="center"
        sx={containerStyles}
        height={`calc(100vh - ${navbarHeight}px)`}
      >
        <Box sx={zonesManagementStyles}>
          <Header />
          <Stack marginTop="2rem">
            <BasicTabs
              renderingStrategy="eager"
              tabs={tabs}
              selectedTab={selectedTabIndex}
              changeTab={changeTab}
              tabsStyles={taskTabStyles}
              customSeperator={<FloatingLine />}
            />
          </Stack>
        </Box>
        <Snackbar open={shouldShow} onClose={closeSnack}>
          {content}
        </Snackbar>
      </Stack>
    </StructureCheck>
  );
};
